import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  Calendar,
  Clock,
  Users,
  MapPin,
  Play,
  Video,
  Radio,
  Settings,
  Save,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_webinar_event_data,
  manage_webinar_status,
  delete_webinar_event,
  webinar_page_config_save,
  webinar_page_config_get,
} from "../serviceconnection/ServiceConnection";

const InputField = ({
  label,
  placeholder,
  type = "text",
  value,
  onChange,
  required,
  textarea,
}) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </label>
    {textarea ? (
      <textarea
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent resize-none"
      />
    ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
      />
    )}
  </div>
);

const EMPTY_CONFIG = {
  hero_badge: "Learn & Connect",
  hero_title: "Webinars &",
  hero_title_highlight: "Events",
  hero_description:
    "Join our expert-led sessions to learn best practices, discover new features, and connect with peers.",
  hero_bg_image: "",
  upcoming_heading: "Upcoming Events",
  upcoming_subheading:
    "Register now to secure your spot in our upcoming sessions.",
  ondemand_heading: "On-Demand Library",
  ondemand_subheading: "Watch our past webinars anytime, anywhere.",
  ondemand_view_all_link: "",
  cta_heading: "Want a Private Session for Your Team?",
  cta_description:
    "We offer customized training and workshops tailored to your organization's specific needs.",
  cta_btn_text: "Request Custom Training",
  cta_btn_link: "",
};

export default function WebinarEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [configModal, setConfigModal] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [config, setConfig] = useState(EMPTY_CONFIG);

  const sc = useCallback(
    (key, val) => setConfig((p) => ({ ...p, [key]: val })),
    [],
  );

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      const res = await server_post_data(get_webinar_event_data, fd);
      setEvents(res.data?.data ?? []);
    } catch {
      showError("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const fd = new FormData();
      const res = await server_post_data(webinar_page_config_get, fd);
      const d = res.data?.data;
      if (d && Object.keys(d).length) setConfig({ ...EMPTY_CONFIG, ...d });
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchConfig();
  }, []);

  const filtered = events.filter((e) => {
    const matchSearch =
      e.title?.toLowerCase().includes(search.toLowerCase()) ||
      e.category?.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "all" || e.event_type === typeFilter;
    return matchSearch && matchType;
  });

  const handleToggle = async (ev) => {
    try {
      const fd = new FormData();
      fd.append("p_id", ev.primary_id);
      fd.append("status", ev.status === 1 ? "0" : "1");
      const res = await server_post_data(manage_webinar_status, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess(res.data?.message);
      setEvents((prev) =>
        prev.map((e) =>
          e.primary_id === ev.primary_id
            ? { ...e, status: ev.status === 1 ? 0 : 1 }
            : e,
        ),
      );
    } catch {
      showError("Something went wrong");
    }
  };

  const handleDelete = async () => {
    try {
      const fd = new FormData();
      fd.append("p_id", deleteId);
      const res = await server_post_data(delete_webinar_event, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Event deleted");
      setDeleteId(null);
      setEvents((prev) => prev.filter((e) => e.primary_id !== deleteId));
    } catch {
      showError("Something went wrong");
    }
  };

  const handleConfigSave = async () => {
    try {
      setConfigLoading(true);
      const fd = new FormData();
      Object.entries(config).forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await server_post_data(webinar_page_config_save, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Page config saved!");
      setConfigModal(false);
    } catch {
      showError("Something went wrong");
    } finally {
      setConfigLoading(false);
    }
  };

  const typeBadge = (type) =>
    type === "upcoming"
      ? {
          bg: "hsl(var(--accent)/0.12)",
          color: "hsl(var(--accent))",
          label: "Upcoming",
        }
      : {
          bg: "hsl(var(--info)/0.12)",
          color: "hsl(var(--info))",
          label: "On-Demand",
        };

  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Webinars & Events</h1>
          <p className="page-subtitle">
            Manage upcoming events and on-demand recordings
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setConfigModal(true)}
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted"
          >
            <Settings className="w-4 h-4" /> Page Config
          </button>
          <button
            onClick={() => navigate("/webinars/add")}
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white"
            style={{ background: "hsl(var(--accent))" }}
          >
            <Plus className="w-4 h-4" /> Add Event
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: events.length, color: "hsl(var(--accent))" },
          {
            label: "Upcoming",
            value: events.filter((e) => e.event_type === "upcoming").length,
            color: "hsl(var(--info))",
          },
          {
            label: "On-Demand",
            value: events.filter((e) => e.event_type === "ondemand").length,
            color: "hsl(var(--warning))",
          },
          {
            label: "Active",
            value: events.filter((e) => e.status === 1).length,
            color: "hsl(var(--success))",
          },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4">
            <p className="text-2xl font-bold" style={{ color: s.color }}>
              {s.value}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="cms-card p-4 flex flex-wrap items-center gap-3">
        <div className="search-input flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or category..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: "all", label: "All" },
            { key: "upcoming", label: "Upcoming" },
            { key: "ondemand", label: "On-Demand" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setTypeFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${typeFilter === f.key ? "text-white border-transparent" : "border-border text-muted-foreground"}`}
              style={
                typeFilter === f.key ? { background: "hsl(var(--accent))" } : {}
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="section-panel">
        <div className="overflow-x-auto">
          <table className="w-full cms-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Type</th>
                <th>Category</th>
                <th>Date / Recorded</th>
                <th>Details</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-10 text-muted-foreground text-sm"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-10 text-muted-foreground text-sm"
                  >
                    No events found
                  </td>
                </tr>
              ) : (
                filtered.map((ev) => {
                  const badge = typeBadge(ev.event_type);
                  return (
                    <tr key={ev.primary_id} className="group">
                      <td>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ background: badge.bg, color: badge.color }}
                          >
                            {ev.event_type === "ondemand" ? (
                              <Play className="w-4 h-4" />
                            ) : (
                              <Radio className="w-4 h-4" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-sm text-foreground line-clamp-1">
                              {ev.title}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {ev.description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{ background: badge.bg, color: badge.color }}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="text-sm text-muted-foreground">
                        {ev.category || "-"}
                      </td>
                      <td>
                        {ev.event_type === "upcoming" && ev.event_date ? (
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(ev.event_date).toLocaleDateString(
                              "en-AU",
                              { day: "numeric", month: "short" },
                            )}
                          </span>
                        ) : ev.recorded_date ? (
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Video className="w-3.5 h-3.5" />
                            {new Date(ev.recorded_date).toLocaleDateString(
                              "en-AU",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <div className="space-y-0.5 text-xs text-muted-foreground">
                          {ev.event_type === "upcoming" ? (
                            <>
                              {ev.event_time && (
                                <p className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {ev.event_time}
                                </p>
                              )}
                              {ev.speakers_count > 0 && (
                                <p className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {ev.speakers_count} speaker
                                  {ev.speakers_count > 1 ? "s" : ""}
                                </p>
                              )}
                              {ev.location && (
                                <p className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {ev.location}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              {ev.duration_minutes > 0 && (
                                <p className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {ev.duration_minutes} min
                                </p>
                              )}
                              {ev.views_count > 0 && (
                                <p className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {ev.views_count} views
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {ev.status === 1 ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                            style={{
                              background: "hsl(var(--success-muted))",
                              color: "hsl(var(--success))",
                            }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />{" "}
                            Active
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
                            style={{
                              background: "hsl(var(--destructive-muted))",
                              color: "hsl(var(--destructive))",
                            }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />{" "}
                            Hidden
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleToggle(ev)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                            style={{
                              color:
                                ev.status === 1
                                  ? "hsl(var(--success))"
                                  : "hsl(var(--warning))",
                            }}
                          >
                            {ev.status === 1 ? (
                              <ToggleRight className="w-4 h-4" />
                            ) : (
                              <ToggleLeft className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() =>
                              navigate(`/webinars/edit/${ev.primary_id}`)
                            }
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteId(ev.primary_id)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                            style={{ color: "hsl(var(--destructive))" }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {events.length} events
          </p>
        </div>
      </div>

      {/* Page Config Modal */}
      {configModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4"
          onClick={() => setConfigModal(false)}
        >
          <div
            className="section-panel p-0 w-full max-w-xl max-h-[92vh] flex flex-col fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header flex-shrink-0">
              <h2 className="section-panel-title">Page Configuration</h2>
              <button
                onClick={() => setConfigModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 section-panel-body space-y-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Hero Section
              </p>
              <InputField
                label="Badge Text"
                placeholder="Learn & Connect"
                value={config.hero_badge}
                onChange={(e) => sc("hero_badge", e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Title"
                  placeholder="Webinars &"
                  value={config.hero_title}
                  onChange={(e) => sc("hero_title", e.target.value)}
                />
                <InputField
                  label="Highlight Word"
                  placeholder="Events"
                  value={config.hero_title_highlight}
                  onChange={(e) => sc("hero_title_highlight", e.target.value)}
                />
              </div>
              <InputField
                label="Description"
                textarea
                value={config.hero_description}
                onChange={(e) => sc("hero_description", e.target.value)}
              />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-2">
                Section Headings
              </p>
              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Upcoming Heading"
                  value={config.upcoming_heading}
                  onChange={(e) => sc("upcoming_heading", e.target.value)}
                />
                <InputField
                  label="On-Demand Heading"
                  value={config.ondemand_heading}
                  onChange={(e) => sc("ondemand_heading", e.target.value)}
                />
              </div>
              <InputField
                label="Upcoming Subheading"
                textarea
                value={config.upcoming_subheading}
                onChange={(e) => sc("upcoming_subheading", e.target.value)}
              />
              <InputField
                label="On-Demand Subheading"
                textarea
                value={config.ondemand_subheading}
                onChange={(e) => sc("ondemand_subheading", e.target.value)}
              />
              <InputField
                label="View All Recordings Link"
                placeholder="https://..."
                value={config.ondemand_view_all_link}
                onChange={(e) => sc("ondemand_view_all_link", e.target.value)}
              />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-2">
                Private Session CTA
              </p>
              <InputField
                label="CTA Heading"
                value={config.cta_heading}
                onChange={(e) => sc("cta_heading", e.target.value)}
              />
              <InputField
                label="CTA Description"
                textarea
                value={config.cta_description}
                onChange={(e) => sc("cta_description", e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Button Text"
                  value={config.cta_btn_text}
                  onChange={(e) => sc("cta_btn_text", e.target.value)}
                />
                <InputField
                  label="Button Link"
                  placeholder="https://..."
                  value={config.cta_btn_link}
                  onChange={(e) => sc("cta_btn_link", e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setConfigModal(false)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfigSave}
                  disabled={configLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "hsl(var(--accent))" }}
                >
                  <Save className="w-4 h-4" />
                  {configLoading ? "Saving..." : "Save Config"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => setDeleteId(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-sm mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header">
              <h2 className="section-panel-title">Delete Event</h2>
            </div>
            <div className="section-panel-body space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: "hsl(var(--destructive))" }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
